import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  MoreVert as MoreVertIcon,
  Link as LinkIcon,
  Reorder as ReorderIcon
} from '@mui/icons-material';

const OfferManagement = ({ authToken }) => {
  const [dialogueBoxes, setDialogueBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDialogueBox, setEditingDialogueBox] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDialogueBox, setSelectedDialogueBox] = useState(null);

  const API_BASE_URL = 'https://petifyshop.mycartly.in';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    offer_text: '',
    button_text: 'CLICK HERE TO DOWNLOAD',
    download_url: '',
    display_order: 0,
    is_active: true
  });

  const fetchDialogueBoxes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/dialogue-box`);
      const data = await response.json();
      
      if (data.success) {
        setDialogueBoxes(data.dialogue_boxes);
      } else {
        setError(data.error || 'Failed to fetch dialogue boxes');
      }
    } catch (err) {
      setError('Failed to fetch dialogue boxes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDialogueBoxes();
  }, []);

  const handleAddDialogueBox = () => {
    setEditingDialogueBox(null);
    setFormData({
      title: '',
      description: '',
      offer_text: '',
      button_text: 'CLICK HERE TO DOWNLOAD',
      download_url: '',
      display_order: dialogueBoxes.length + 1,
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleEditDialogueBox = (dialogueBox) => {
    setEditingDialogueBox(dialogueBox);
    setFormData({
      title: dialogueBox.title,
      description: dialogueBox.description || '',
      offer_text: dialogueBox.offer_text || '',
      button_text: dialogueBox.button_text || 'CLICK HERE TO DOWNLOAD',
      download_url: dialogueBox.download_url || '',
      display_order: dialogueBox.display_order,
      is_active: dialogueBox.is_active
    });
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteDialogueBox = async (dialogueBoxId) => {
    if (!window.confirm('Are you sure you want to delete this dialogue box?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/dialogue-box/${dialogueBoxId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Dialogue box deleted successfully');
        fetchDialogueBoxes();
      } else {
        setError(data.error || 'Failed to delete dialogue box');
      }
    } catch (err) {
      setError('Failed to delete dialogue box: ' + err.message);
    }
    setAnchorEl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDialogueBox 
        ? `${API_BASE_URL}/admin/dialogue-box/${editingDialogueBox.id}`
        : `${API_BASE_URL}/admin/dialogue-box`;
      
      const method = editingDialogueBox ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingDialogueBox ? 'Dialogue box updated successfully' : 'Dialogue box added successfully');
        setDialogOpen(false);
        fetchDialogueBoxes();
      } else {
        setError(data.error || 'Failed to save dialogue box');
      }
    } catch (err) {
      setError('Failed to save dialogue box: ' + err.message);
    }
  };

  const handleMenuOpen = (event, dialogueBox) => {
    setAnchorEl(event.currentTarget);
    setSelectedDialogueBox(dialogueBox);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDialogueBox(null);
  };

  const toggleDialogueBoxStatus = async (dialogueBox) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/dialogue-box/${dialogueBox.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...dialogueBox,
          is_active: !dialogueBox.is_active
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Dialogue box ${dialogueBox.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchDialogueBoxes();
      } else {
        setError(data.error || 'Failed to update dialogue box status');
      }
    } catch (err) {
      setError('Failed to update dialogue box status: ' + err.message);
    }
    setAnchorEl(null);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Offer Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDialogueBox}
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          Add Offer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearMessages} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={clearMessages} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Offer Text</TableCell>
              <TableCell>Download URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dialogueBoxes.map((dialogueBox) => (
              <TableRow key={dialogueBox.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReorderIcon sx={{ color: 'text.secondary' }} />
                    {dialogueBox.display_order}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {dialogueBox.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                    {dialogueBox.offer_text || 'No offer text'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {dialogueBox.download_url ? (
                    <Tooltip title="View Download URL">
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        href={dialogueBox.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No URL
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={dialogueBox.is_active ? 'Active' : 'Inactive'}
                    color={dialogueBox.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(dialogueBox.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, dialogueBox)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditDialogueBox(selectedDialogueBox)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => toggleDialogueBoxStatus(selectedDialogueBox)}>
          <ListItemIcon>
            {selectedDialogueBox?.is_active ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedDialogueBox?.is_active ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteDialogueBox(selectedDialogueBox?.id)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDialogueBox ? 'Edit Offer' : 'Add New Offer'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                fullWidth
                helperText="Main heading for the dialogue box (e.g., 'DOWNLOAD OUR APP AND GET ADDITIONAL INR 100 OFF')"
              />
              
              <TextField
                label="Offer Text"
                value={formData.offer_text}
                onChange={(e) => setFormData({ ...formData, offer_text: e.target.value })}
                fullWidth
                helperText="Secondary text (e.g., 'DOWNLOAD TODAY')"
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
                helperText="Additional description or details"
              />
              
              <TextField
                label="Button Text"
                value={formData.button_text}
                onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                fullWidth
                helperText="Text for the download button"
              />
              
              <TextField
                label="Download URL"
                value={formData.download_url}
                onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                fullWidth
                helperText="URL where users will be redirected when they click the button"
              />
              
              <TextField
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                fullWidth
                helperText="Lower numbers appear first"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
              {editingDialogueBox ? 'Update' : 'Add'} Offer
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default OfferManagement;
